import { useMutation, useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { useState } from 'react';
import HyperModal from 'react-hyper-modal';
import { useTranslation } from 'react-i18next';
import Lottie from 'lottie-react';
import * as unlockAnimationData from '../../../assets/lottie/unlock.json';
import * as chainAnimationData from '../../../assets/lottie/chain-lock.json';
import { LatestEventQuery } from '../../../graphql/events/latestEventQuery';
import { UpdateEventMutation } from '../../../graphql/events/updateEventMutation';
import { GetOngoingParkingSessionQuery } from '../../../graphql/sessions/getOngoingParkingSessionQuery';
import { CreateRewardCustomMutation } from '../../../graphql/rewards/createRewardCustomMutation';
import { useAuth } from '../../../lib/auth';
import {
  LatestEventType,
  ParkingSession,
  ParkingTypeEnum,
} from '../../../utils/utilTypes';
import {
  getActiveSessionFromLatestEvent,
  getLabelsForParkingType,
} from '../../../utils/utilsParking';
import { toastManager } from '../../../utils/utilsToast';
import { AppHero } from '../AppHero';
import { Button } from '../Button';
import { ReportIssueModal } from '../ReportIssueModal';
import { UnlockModalProps } from './props';
import { GetParkingByIdQuery } from '../../../graphql/parkings/getParkingByIdQuery';
import { EndParkingSessionMutation } from '../../../graphql/sessions/endParkingsessionMutation';
import { GetParkingsWithinDistance } from '../../../graphql/parkings/getParkingsWithinDistance';
import { checkIfGateIsSingular } from '../../../utils/utilGates';

export const UnlockModal = ({ parkingDetail, onClose }: UnlockModalProps) => {
  const router = useRouter();
  const { currentUser, getCurrentUser } = useAuth();
  const [isParkingIssueModalShown, setIsParkingIssueModalShown] =
    useState<boolean>(false);
  const { t } = useTranslation();

  const { data: latestEventData } = useQuery(LatestEventQuery);
  const [updateEvent] = useMutation(UpdateEventMutation);
  const [createReward, { loading: isLoadingRewardCreate }] = useMutation(
    CreateRewardCustomMutation,
  );
  const [isLoadingSessionOperation, setIsLoadingSessionOperation] =
    useState<boolean>(false);

  const [endParkingSession, { loading: isLoadingEndParkingSession }] =
    useMutation(EndParkingSessionMutation, {
      refetchQueries: [
        GetParkingByIdQuery,
        GetOngoingParkingSessionQuery,
        GetParkingsWithinDistance,
      ],
    });

  const acknowledgeParkingEvent = async (): Promise<
    LatestEventType | undefined
  > => {
    try {
      const response = await updateEvent({
        variables: {
          id: latestEventData.latestEvent.id,
          data: {
            isAknowledgedByUser: true,
          },
        },
      });

      await getCurrentUser();

      return response.data.updateEvent as LatestEventType;
    } catch (err: any) {
      toastManager.couldNotAcknowledgeParkingEvent();
    }
  };

  const handleCloseIssueModal = async () => {
    setIsParkingIssueModalShown(false);
    await acknowledgeParkingEvent();
  };

  const handlePostUnlock = async (
    updatedEvent: LatestEventType | undefined,
  ) => {
    if (parkingDetail.referencedBy.length > 0) {
      const [nextParking] = parkingDetail.referencedBy;
      await router.push(`/?parkingId=${nextParking.id}`);
      return;
    }

    if (updatedEvent && updatedEvent?.completedQuests.length > 0) {
      const uncollectedQuest = updatedEvent.completedQuests[0];
      await router.push(`/quest-rewards/${uncollectedQuest?.id}`);
      return;

      // // TODO: same as with reward
      // console.log('REDIRECT TO QUEST REWARD: ', uncollectedQuest);
      // if (uncollectedQuest) {
      //   await router.push(`/quest-rewards/${uncollectedQuest?.id}`);
      //   return;
      // }
    }

    // Single Gate -> Multi Gate Flow (close modal and clear cache as it is confusing to keep it on)
    if (
      updatedEvent?.parking?.referenceParking ||
      !parkingDetail.referenceParking
    ) {
      await onClose();
    }

    // const isParkingCampaignActive = parkingDetail?.campaigns?.find(
    //   (campaign: CampaignType) => {
    //     const now = new Date();
    //     const startDate = new Date(campaign.startsAt);
    //     const endDate = new Date(campaign.endsAt);
    //     return now > startDate && now < endDate;
    //   }
    // );
    // if (isParkingCampaignActive) {
    //   const resp = await createReward({
    //     variables: {
    //       campaignId: isParkingCampaignActive.id,
    //     },
    //   });
    //   if (resp?.data?.createRewardCustom?.id) {
    //     await router.push(`/rewards/${resp?.data?.createRewardCustom.id}`);
    //   }
    // }
  };

  const handleAcknowledgeEvent = async () => {
    try {
      const updatedEvent = await acknowledgeParkingEvent();
      await handlePostUnlock(updatedEvent);
    } catch (err) {
      console.log(err);
    } finally {
      // setHideModal(true);
    }
  };

  const handleEndParkingSessionOnSingularGateClick = async (
    ongoingSession: ParkingSession,
  ) => {
    try {
      setIsLoadingSessionOperation(true);
      await endParkingSession({
        variables: {
          id: ongoingSession.id,
        },
      });

      const updatedEvent = await acknowledgeParkingEvent();
      await handlePostUnlock(updatedEvent);
    } catch (err: any) {
      console.log(err.message);
    } finally {
      setTimeout(() => {
        setIsLoadingSessionOperation(false);
      }, 1000);
    }
  };

  const renderAcknowledgeButton = () => {
    return (
      <Button
        className="bg-white flex justify-center items-center"
        loading={isLoadingRewardCreate}
        onClick={handleAcknowledgeEvent}
        type="primary"
      >
        <span className="text-primary">{t('confirm')}</span>
      </Button>
    );
  };

  const renderHeaders = () => {
    const ongoingSession = getActiveSessionFromLatestEvent(
      latestEventData?.latestEvent,
      currentUser,
    );

    const labels = getLabelsForParkingType(
      t,
      latestEventData?.latestEvent?.parking?.type as ParkingTypeEnum,
    );

    let content = null;

    if (ongoingSession) {
      content = (
        <>
          <div className="text-white text-xl xxs:text-2xl text-center font-bold">
            {labels.title}
          </div>
          {labels.description && (
            <div
              className="text-white text-center w-2/3 mx-auto text-sm xxs:text-md "
              dangerouslySetInnerHTML={{ __html: labels.description }}
            ></div>
          )}
        </>
      );
    } else {
      content = (
        <>
          <div className="text-white text-xl xxs:text-2xl text-center font-bold">
            {labels.title}
          </div>
          {labels.description && (
            <div
              className="text-white text-center w-2/3 mx-auto text-sm xxs:text-md "
              dangerouslySetInnerHTML={{ __html: labels.description }}
            ></div>
          )}
        </>
      );
    }

    return (
      <>
        <div className="ml-6 mt-6 flex justify-center">
          <div className="w-24 xxs:w-48 h-24 xxs:h-48">
            <Lottie
              animationData={
                parkingDetail?.type === 'rental'
                  ? chainAnimationData
                  : unlockAnimationData
              }
              loop
            />
          </div>
        </div>

        {content}
      </>
    );
  };

  const renderSingleGateFlowButtons = () => {
    const ongoingSession = getActiveSessionFromLatestEvent(
      latestEventData?.latestEvent,
      currentUser,
    );

    const labels = getLabelsForParkingType(
      t,
      latestEventData?.latestEvent?.parking?.type as ParkingTypeEnum,
    );

    if (ongoingSession) {
      return (
        <div className="flex flex-col gap-4">
          <Button
            type="primary"
            className="bg-white flex justify-center items-center"
            loading={isLoadingEndParkingSession}
            disabled={isLoadingEndParkingSession}
            onClick={() =>
              handleEndParkingSessionOnSingularGateClick(ongoingSession)
            }
          >
            <span className="text-primary"> {labels.lock}</span>
          </Button>
          <Button
            className="border-white flex justify-center items-center"
            loading={isLoadingRewardCreate}
            onClick={handleAcknowledgeEvent}
            type="outline"
          >
            <span className="text-white">
              {parkingDetail?.type === 'rental'
                ? t('rentalContinueCase')
                : t('close')}
            </span>
          </Button>
          {parkingDetail?.type === 'rental' && (
            <p className="text-gray-dark text-xs text-center">
              {t('rentalCaseNote')}
            </p>
          )}
        </div>
      );
    }

    return renderAcknowledgeButton();
  };

  if (!latestEventData) {
    return null;
  }

  let canShow = false;

  if (latestEventData?.latestEvent) {
    const le = latestEventData.latestEvent;

    if (le.type === 'manual') {
      if (
        (le.status === 'mqtt' ||
          le.status === 'sim_completed' ||
          le.status === 'internal_testing_purposes') &&
        !le.isAknowledgedByUser
      ) {
        canShow = true;
      }
    }
  }

  const isSingleGateFlow = checkIfGateIsSingular(
    latestEventData?.latestEvent?.gate,
  );

  const ongoingSession = getActiveSessionFromLatestEvent(
    latestEventData?.latestEvent,
    currentUser,
  );

  return (
    <HyperModal
      isOpen={canShow}
      requestClose={() => {}}
      closeOnCloseIconClick={false}
      isFullscreen={true}
      renderCloseIcon={() => null}
      closeOnEscClick={false}
    >
      <div className="bg-primary h-full overflow-scroll p-6 flex flex-col gap-4">
        <div>
          <AppHero inverted />
        </div>

        {renderHeaders()}

        <div
          className={`flex flex-col gap-4 flex-1  justify-end ${
            isLoadingSessionOperation ? 'opacity-50 pointer-events-none' : ''
          }`}
        >
          {isSingleGateFlow || ongoingSession
            ? renderSingleGateFlowButtons()
            : renderAcknowledgeButton()}

          <Button
            className="text-white underline"
            type="link"
            onClick={() => setIsParkingIssueModalShown(true)}
          >
            {t('somethingIsWrong')}
          </Button>
        </div>
      </div>
      <ReportIssueModal
        isOpen={isParkingIssueModalShown}
        onClose={handleCloseIssueModal}
      />
    </HyperModal>
  );
};
